import { Component, Input, OnChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ILevel } from '@gray/domain/models/user/level.model';
import { INaviRouteMarker, INaviRouteStep } from '@gray/ui/components/navi-route-map/navi-route-map.model';
import { IStudentExamOverview } from '../../../core/models/student-overview.model';

interface ILevelRange {
  min: number | null;
  max: number | null;
}

/**
 * The student's NaviDerece route for one exam: from the level they started at up to the highest
 * one, with their current score marking where they stand.
 *
 * Only the labels are worked out here; the drawing, the colours and the animation belong to the
 * shared map, which the NaviDerece module can reuse with different labels later.
 */
@Component({
  selector: 'coach-navi-route-card',
  templateUrl: './navi-route-card.component.html',
  styleUrl: './navi-route-card.component.scss',
})
export class NaviRouteCardComponent implements OnChanges {
  /** Every level, ordered or not, each carrying its per-exam score ranges. */
  @Input() levels: ILevel[] = [];
  /** The exam whose ranges and score the route is drawn from. */
  @Input() examId: number | null = null;
  @Input() examName = '';
  @Input() exam?: IStudentExamOverview | null;
  @Input() studentName?: string | null;

  steps: INaviRouteStep[] = [];
  activeIndex = 0;
  progressToNext = 0;
  marker: INaviRouteMarker | null = null;

  constructor(
    private translate: TranslateService,
  ) {
  }

  ngOnChanges(): void {
    this.buildRoute();
  }

  currentLevelName(): string | null {
    return this.levelName(this.steps[this.activeIndex]?.levelKey);
  }

  nextLevelName(): string | null {
    return this.levelName(this.steps[this.activeIndex + 1]?.levelKey);
  }

  hasRoute(): boolean {
    return this.steps.length > 1;
  }

  private buildRoute() {
    // Rank counts down as the level goes up (1 = Kırmızı, 6 = Pembe), so the road is walked from
    // the highest rank to the lowest, and "up to the top level" means every rank at or above it.
    const ordered = [...this.levels].sort((first, second) => second.rank - first.rank);
    const lowestRank = ordered[0]?.rank ?? 0;
    // Where the student started, or the bottom of the scale when onboarding recorded nothing. A
    // student who has since dropped below that level still has to appear on their own route, so
    // the road starts at whichever of the two is lower (the higher rank).
    const initialRank = this.exam?.initialLevel?.rank ?? lowestRank;
    const currentRank = this.exam?.level?.rank ?? initialRank;
    const startRank = Math.max(initialRank, currentRank);
    const fromStart = ordered.filter(level => level.rank <= startRank);
    // A student who started at the top level would be left with a single stop and no road at all;
    // they get the whole scale instead, with their pin at the end of it.
    const route = fromStart.length > 1 ? fromStart : ordered;

    this.steps = route.map((level, index) => ({
      id: level.id,
      levelKey: level.key,
      topLabel: this.translate.instant(`levels.${level.key}`),
      bottomLabel: this.milestoneLabel(level, index === route.length - 1),
      bottomSuffix: this.translate.instant('students.navi_route.net'),
    }));

    const currentIndex = route.findIndex(level => level.id === this.exam?.level?.id);
    this.activeIndex = currentIndex >= 0 ? currentIndex : 0;
    this.progressToNext = this.levelProgress(route[this.activeIndex]);
    this.marker = this.buildMarker();
  }

  /** The net a level starts at; the last one has no ceiling, so it reads as "100+". */
  private milestoneLabel(level: ILevel, isLast: boolean): string | null {
    const range = this.rangeOf(level);
    if (range.min === null) {
      return null;
    }

    const label = this.formatScore(range.min);
    return (isLast || range.max === null) ? `${label}+` : label;
  }

  /** How far into the current level the student is — what the map turns into a pin position. */
  private levelProgress(level?: ILevel): number {
    const score = this.exam?.score;
    if (!level || score === null || score === undefined) {
      return 0;
    }

    const range = this.rangeOf(level);
    if (range.min === null || range.max === null || range.max <= range.min) {
      return 100;
    }

    const progress = ((score - range.min) / (range.max - range.min)) * 100;
    return Math.max(0, Math.min(100, Math.round(progress)));
  }

  private buildMarker(): INaviRouteMarker | null {
    const score = this.exam?.score;
    if (score === null || score === undefined) {
      return null;
    }

    return {
      label: this.translate.instant('students.navi_route.current_position', {name: this.firstName()}),
      value: `${this.formatScore(score)} ${this.translate.instant('students.navi_route.net')}`,
      badge: this.examName,
    };
  }

  private rangeOf(level: ILevel): ILevelRange {
    const examScore = level.examScores?.find(score => score.examId === this.examId);
    return {
      min: examScore?.minScore ?? null,
      max: examScore?.maxScore ?? null,
    };
  }

  private firstName(): string {
    return this.studentName?.trim().split(' ')[0] ?? '';
  }

  private levelName(key?: string): string | null {
    return key ? this.translate.instant(`levels.${key}`) : null;
  }

  private formatScore(score: number): string {
    return score.toLocaleString('tr-TR');
  }
}
